<script setup>
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'

import { AppIcon } from '@/components/shared'
import { useAuth } from '@/composables/useAuth'
import { ROLE_ICONS, ROLE_LABELS, ROLE_TO_ZONE } from '@/constants/roles'
import { ZONE_META } from '@/constants/zones'

/**
 * Compact role-pill row shown in the navbar. Each pill corresponds to one
 * role the signed-in user holds - clicking it jumps to that role's zone
 * landing page. The pill matching the user's *current* zone is highlighted.
 *
 * Why roles and not zones? The user-facing identity is "I am a rider +
 * photographer + (future) car enthusiast". Same-zone roles (rider + car
 * enthusiast) each get their own pill because conceptually the user sees
 * themselves as two personas - even if they currently navigate to the same
 * landing path. Once content filtering by role context lands, the pills
 * will diverge to different views.
 *
 * Hidden when:
 *   - user is a guest
 *   - user only has one role (nothing to switch between)
 *   - user is the admin-only (no zone switcher is useful for admins)
 */
const { user } = useAuth()
const page = usePage()

const currentZone = computed(() => {
    const url = page.url
    if (url.startsWith('/studio')) return 'studio'
    if (url.startsWith('/dashboard')) return 'admin'
    if (url.startsWith('/profil')) return 'profile'
    return null // on public pages (/galerie, /mista, /) no pill is "active"
})

// Show one pill per role the user holds, dropping any role that isn't
// defined in the ROLES constant (defensive against DB drift).
const pills = computed(() => {
    const roles = user.value?.roles ?? []

    return roles
        .filter((role) => role in ROLE_TO_ZONE && role in ROLE_LABELS)
        .map((role) => ({
            role,
            label: ROLE_LABELS[role],
            icon: ROLE_ICONS[role],
            zone: ROLE_TO_ZONE[role],
            href: ZONE_META[ROLE_TO_ZONE[role]].href,
        }))
})

// Always visible for authenticated users - acts as a status indicator
// ("I'm here as a Fotograf") for single-role users and as a zone switcher
// for multi-role users. The role-conditional `navLinks` (Studio/Garáž) in
// Navbar.vue are intentionally dropped to avoid duplication.
const visible = computed(() => pills.value.length > 0)
</script>

<template>
    <!--
        Compact role-switcher. Visual hierarchy choice:
          - ACTIVE pill: full icon + label, orange-filled (status indicator).
            Reads as "I am here as a Fotograf".
          - INACTIVE pills: icon-only, ~40 px wide, neutral zinc. Hover
            expands to show label as tooltip (native `title`). Reads as
            "click to switch to that role's zone".

        Saves ~140 px of horizontal space vs. all-equal pills and removes
        the orange-on-orange conflict with the "Nahrát fotky" primary CTA
        sitting next to it. Multi-role users still see all their roles;
        single-role users (1 pill total) get a clean status badge.
    -->
    <div
        class="hidden items-center gap-1 rounded-full border border-zinc-800 bg-zinc-900/50 p-1 md:flex"
        v-if="visible"
        role="group"
        aria-label="Přepnout roli"
    >
        <Link
            class="teko-font flex items-center justify-center gap-2 rounded-full font-bold tracking-wider uppercase transition-all"
            v-for="pill in pills"
            :key="pill.role"
            :href="pill.href"
            :class="[
                pill.zone === currentZone
                    ? 'bg-primary-container text-on-primary-container px-3 py-1.5 text-sm shadow-sm'
                    : 'h-8 w-8 text-sm text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200',
            ]"
            :title="`Přejít do zóny ${pill.label}`"
            :aria-label="`Přepnout do zóny ${pill.label}`"
        >
            <AppIcon
                class="text-base"
                :name="pill.icon"
            />
            <span v-if="pill.zone === currentZone">{{ pill.label }}</span>
        </Link>
    </div>
</template>
